import { UploadOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import { Button, Col, Divider, Form, message, Modal, Row, Upload } from "antd";
import { useState } from "react";
import { FiUpload } from "react-icons/fi";

import { uploadFormCsv } from "../../_store";

export const UploadFormCsvModal = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const showModal = () => setIsModalOpen(true);

  const handleCancel = () => {
    setIsModalOpen(false);
    setFile(null);
  };

  const onFinish = async () => {
    if (file) {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await uploadFormCsv(messageApi, formData);
        if (response) {
          handleCancel();
        }
      } catch (error) {
        console.error("CSV Upload Error:", error);
        messageApi.error("Failed to upload CSV");
      } finally {
        setLoading(false);
      }
    } else {
      messageApi.error("Please upload a CSV file");
    }
  };

  const handleDownload = () => {
    const fileName = "form_upload_format.csv";
    const link = document.createElement("a");
    link.href = `/${fileName}`;
    link.download = fileName;
    link.click();
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      setFile(file);
      return false;
    },
    onRemove: () => {
      setFile(null);
    },
    accept: ".csv",
    maxCount: 1,
  };

  return (
    <>
      {contextHolder}
      <Button
        icon={<FiUpload />}
        onClick={showModal}
        style={{ background: "transparent" }}
      >
        Upload CSV
      </Button>

      <Modal
        forceRender
        title="Upload CSV"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={false}
        centered
        width={750}
      >
        <Divider />
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          requiredMark={false}
          autoComplete="off"
        >
          <Row gutter={[20, 0]}>
            <Col xs={24}>
              <Form.Item label="">
                <Upload {...uploadProps}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: "100%" }}
                    size="large"
                  >
                    Upload CSV
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <p>
                Download a{" "}
                <Button
                  type="text"
                  style={{
                    color: "var(--color-primary)",
                    paddingInline: "5px",
                  }}
                  onClick={handleDownload}
                >
                  sample CSV template
                </Button>{" "}
                to see an example of the format required.
              </p>
            </Col>

            <Col xs={24}>
              <p style={{ marginTop: "10px" }}>
                <b>Upload CSV for Forms</b>
              </p>
              <p>
                Select a CSV file to upload forms. The CSV should contain
                columns with headers in this order:
              </p>
              <ul style={{ paddingLeft: "18px", marginTop: "10px" }}>
                <li>package (required) - Package name</li>
                <li>Category (required) - Category name</li>
                <li>sub-category (optional) - Sub-category name</li>
                <li>title (required) - Form title</li>
                <li>description (optional) - Form description</li>
                <li>
                  url (required) - Google Docs URL containing form content
                </li>
              </ul>
              <p style={{ fontSize: "12px" }}>
                <b>Note:</b> The CSV file should contain headers in the first
                row. Google Docs URLs will be processed to extract text content
                for the form body.
              </p>
            </Col>

            <Col xs={24} style={{ marginTop: "10px" }}>
              <Form.Item>
                <Button type="primary" onClick={handleCancel}>
                  Cancel
                </Button>
                &nbsp; &nbsp;
                <Button loading={loading} type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
